import React, { useState } from "react";
import { Radio, Tag } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { RefChip } from "./RefChip";
import {
  CustomControl,
  canCustom,
  flippedValue,
  show,
  valueKey,
} from "../configFieldControl";

const CUSTOM = "__custom__";
const OID = /^[a-f0-9]{24}$/i;

const isEmpty = v =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/** The value the result gets today: the decision, else (no conflict) the suggestion / the single value. */
export const resultOf = (f, distinct, chosen) =>
  chosen !== undefined
    ? chosen
    : f.conflict
    ? undefined
    : f.suggested !== undefined
    ? f.suggested
    : distinct[0]
    ? distinct[0].value
    : undefined;

/**
 * State of one field, in the words shown on its tag:
 * pending (« à choisir »), manual (« choix manuel »), typed (« valeur saisie »),
 * suggestion, same (« identique partout »).
 */
export const fieldStatus = ({ required, conflict, chosen, distinct }) => {
  const decided = chosen !== undefined;
  if (required && !decided) return "pending";
  if (decided)
    return distinct.some(d => d.key === valueKey(chosen)) ? "manual" : "typed";
  return conflict ? "suggestion" : "same";
};

const STATUS = {
  pending: { word: "à choisir", cls: "cfg-tag--pending" },
  manual: { word: "choix manuel", cls: "cfg-tag--manual" },
  typed: { word: "valeur saisie", cls: "cfg-tag--typed" },
  suggestion: { word: "suggestion", cls: "cfg-tag--same" },
  same: { word: "identique partout", cls: "cfg-tag--same" },
};

/** « à choisir » / « choix manuel » / « valeur saisie » / « identique partout » pill (an antd Tag). */
export function StatusTag({ status }) {
  const s = STATUS[status];
  if (!s) return null;
  return (
    <Tag
      className={`cfg-tag ${s.cls}`}
      icon={status === "pending" ? <ExclamationCircleOutlined /> : null}
    >
      {s.word}
    </Tag>
  );
}

/** A configuration value as a person reads it: names for references, « oui / non », « vide ». */
export function ConfigValue({ meta = {}, value, names = {}, struck, strong }) {
  if (isEmpty(value)) return <span className="sv-empty">vide</span>;
  const refOf = id => ({
    id: String(id),
    name: names[id] || "fiche introuvable",
  });
  if (Array.isArray(value))
    return (
      <span className="sv-list">
        {value.map(id => (
          <span key={String(id)} className="sv-list-item">
            {OID.test(String(id)) ? (
              <RefChip refValue={refOf(id)} struck={struck} strong={strong} />
            ) : (
              String(id)
            )}
          </span>
        ))}
      </span>
    );
  if (
    typeof value === "string" &&
    OID.test(value) &&
    (meta.kind === "ref" || names[value])
  )
    return <RefChip refValue={refOf(value)} struck={struck} strong={strong} />;
  let text = show(value, names);
  if (meta.kind === "enum" && Array.isArray(meta.options)) {
    const o = meta.options.find(x => x.value === value);
    if (o && o.label) text = o.label;
  }
  return (
    <span
      className={struck ? "sv-val--old" : strong ? "sv-val--new" : "sv-val"}
    >
      {text}
    </span>
  );
}

const who = (d, total) =>
  d.holders.length === 1
    ? d.holders[0]
    : d.holders.length === total && total > 2
    ? `Les ${total} établissements`
    : `${d.holders.length} établissements`;

/** Left line: « Malévoz - La Forêt → psychiatrie stationnaire » / « 5 établissements : Santé ». */
function BeforeLine({ d, total, meta, names, result }) {
  const decided = result !== undefined;
  const kept = decided && valueKey(result) === d.key;
  const replaced = decided && !kept;
  const group = d.holders.length > 1;
  return (
    <div
      className={`cfg-line cfg-line--before${kept ? " is-kept" : ""}${
        replaced ? " is-replaced" : ""
      }`}
    >
      <div className="cfg-line-main">
        <span className="cfg-who">
          {who(d, total)}
          {group ? " :" : ""}
        </span>
        {!group && <ArrowRightOutlined className="cfg-arrow" />}
        <span className="cfg-value">
          <ConfigValue
            meta={meta}
            value={d.value}
            names={names}
            struck={replaced}
          />
        </span>
        {kept && (
          <span className="cfg-kept" title="cette valeur est gardée">
            <CheckOutlined /> gardée
          </span>
        )}
        {replaced && (
          <span className="cfg-replaced" title="cette valeur est remplacée">
            remplacée
          </span>
        )}
      </div>
      {group && <div className="cfg-holders">{d.holders.join(" · ")}</div>}
    </div>
  );
}

function Head({ side, meta, sub, ribbon }) {
  return (
    <div className={`cfg-head cfg-head--${side}`}>
      {ribbon && (
        <div className={`sv-ribbon sv-ribbon--${side}`}>
          {side === "after" ? "APRÈS" : "AVANT"}
        </div>
      )}
      <div className="cfg-head-title">{meta.label}</div>
      {sub && <div className="cfg-head-sub">{sub}</div>}
    </div>
  );
}

/**
 * One configuration field as a pair of cards. AVANT: every establishment's value today, equal
 * values grouped. APRÈS: one radio per distinct value on the same row as its « avant » line,
 * « Autre… » revealing the control of the field's kind, then the resulting value, big, with its
 * tag. The container carries `data-field` (the field key) and, while a choice is still possible,
 * `data-decide` — the walkthrough's hooks.
 */
export function ConfigPair({
  meta,
  f,
  distinct,
  chosen,
  required,
  names,
  options,
  disabled,
  decide,
  stacked,
}) {
  const decided = chosen !== undefined;
  const chosenKey = decided ? valueKey(chosen) : null;
  const isCustomValue = decided && !distinct.some(d => d.key === chosenKey);
  // « Autre… » stays open while the operator works in it (a value typed equal to an existing
  // one, or a tag list passing through an existing set, must not make the control vanish)
  const [customOpen, setCustomOpen] = useState(isCustomValue);
  const allowCustom = canCustom(meta, options, distinct);
  const custom = (isCustomValue || customOpen) && allowCustom;
  const selected = custom
    ? CUSTOM
    : decided
    ? chosenKey
    : f.conflict
    ? null
    : distinct[0]
    ? distinct[0].key
    : null;

  const pick = key => {
    if (key !== CUSTOM) {
      setCustomOpen(false);
      const d = distinct.find(x => x.key === key);
      if (d) decide(d.value);
      return;
    }
    setCustomOpen(true);
    // a boolean has exactly one other state: choosing « Autre… » is choosing it
    if (meta.kind === "boolean" || meta.kind === "notification")
      decide(flippedValue(meta, distinct));
  };

  const result = resultOf(f, distinct, chosen);
  const status = fieldStatus({
    required,
    conflict: f.conflict,
    chosen,
    distinct,
  });
  const total = distinct.reduce((n, d) => n + d.holders.length, 0);
  const beforeSub = f.conflict
    ? `${distinct.length} valeurs différentes`
    : "même valeur partout";
  const afterSub =
    status === "pending"
      ? "Quelle valeur garder ?"
      : status === "same"
      ? "la valeur commune est reprise"
      : "votre décision";

  const radioRow = d => (
    <Radio
      disabled={disabled}
      checked={selected === d.key}
      onChange={() => pick(d.key)}
      className="cfg-radio"
    >
      <span className="cfg-radio-value">
        <ConfigValue meta={meta} value={d.value} names={names} />
      </span>
      <span className="cfg-radio-who">
        {distinct.length > 1
          ? `comme ${who(d, total).replace(/^Les /, "les ")}`
          : `tous les établissements (${d.holders.length})`}
      </span>
    </Radio>
  );
  const customRow = allowCustom && (
    <div className="cfg-custom">
      <Radio
        disabled={disabled}
        checked={selected === CUSTOM}
        onChange={() => pick(CUSTOM)}
        className="cfg-radio"
      >
        <span className="cfg-radio-value">Autre…</span>
        <span className="cfg-radio-who">
          une valeur qu'aucun établissement n'a
        </span>
      </Radio>
      {custom && (
        <div className="cfg-custom-control">
          <CustomControl
            meta={meta}
            options={options}
            value={decided ? chosen : undefined}
            before={distinct.map(d => d.value)}
            disabled={disabled}
            onSave={decide}
          />
        </div>
      )}
    </div>
  );
  const resultBox = (
    <div className={`cfg-result cfg-result--${status}`}>
      <div className="cfg-result-label">Résultat :</div>
      <div className="cfg-result-body">
        <span className="cfg-result-value">
          {result === undefined ? (
            <span className="cfg-waiting">en attente d'un choix</span>
          ) : (
            <ConfigValue meta={meta} value={result} names={names} strong />
          )}
        </span>
        <StatusTag status={status} />
      </div>
      {status === "typed" && (
        <div className="cfg-result-note">
          choix manuel — aucun établissement n'avait cette valeur
        </div>
      )}
    </div>
  );

  const cls = `cfg-pair cfg-pair--${status} ${
    stacked ? "cfg-pair--stacked" : "cfg-pair--wide"
  }`;
  const attrs = {
    className: cls,
    "data-field": meta.field,
    ...(allowCustom || distinct.length > 1 ? { "data-decide": "" } : {}),
  };

  if (stacked)
    return (
      <div {...attrs}>
        <div className="cfg-card cfg-card--before">
          <Head side="before" meta={meta} sub={beforeSub} ribbon />
          <div className="cfg-lines">
            {distinct.map(d => (
              <BeforeLine
                key={d.key}
                d={d}
                total={total}
                meta={meta}
                names={names}
                result={result}
              />
            ))}
          </div>
        </div>
        <div className="cfg-card cfg-card--after">
          <Head side="after" meta={meta} sub={afterSub} ribbon />
          <div className="cfg-lines">
            {distinct.map(d => (
              <div
                key={d.key}
                className={`cfg-line cfg-line--after${
                  selected === d.key ? " is-selected" : ""
                }`}
              >
                {radioRow(d)}
              </div>
            ))}
            {customRow && (
              <div
                className={`cfg-line cfg-line--after${
                  selected === CUSTOM ? " is-selected" : ""
                }`}
              >
                {customRow}
              </div>
            )}
            {resultBox}
          </div>
        </div>
      </div>
    );

  // wide: one grid, row 1 = heads, one row per distinct value (its « avant » line facing its
  // radio), then « Autre… » and the result on the right
  const extra = (customRow ? 1 : 0) + 1;
  const rows = 1 + distinct.length + extra;
  return (
    <div {...attrs} style={{ gridTemplateRows: `repeat(${rows}, auto)` }}>
      <div
        className="cfg-bg cfg-bg--before"
        style={{ gridColumn: 1, gridRow: `1 / span ${rows}` }}
      />
      <div
        className="cfg-bg cfg-bg--after"
        style={{ gridColumn: 2, gridRow: `1 / span ${rows}` }}
      />
      <div className="cfg-cell" style={{ gridColumn: 1, gridRow: 1 }}>
        <Head side="before" meta={meta} sub={beforeSub} />
      </div>
      <div className="cfg-cell" style={{ gridColumn: 2, gridRow: 1 }}>
        <Head side="after" meta={meta} sub={afterSub} />
      </div>
      {distinct.map((d, i) => (
        <React.Fragment key={d.key}>
          <div
            className="cfg-cell cfg-cell--line"
            style={{ gridColumn: 1, gridRow: i + 2 }}
          >
            <BeforeLine
              d={d}
              total={total}
              meta={meta}
              names={names}
              result={result}
            />
          </div>
          <div
            className="cfg-cell cfg-cell--line"
            style={{ gridColumn: 2, gridRow: i + 2 }}
          >
            <div
              className={`cfg-line cfg-line--after${
                selected === d.key ? " is-selected" : ""
              }`}
            >
              {radioRow(d)}
            </div>
          </div>
        </React.Fragment>
      ))}
      {customRow && (
        <div
          className="cfg-cell cfg-cell--line"
          style={{ gridColumn: 2, gridRow: distinct.length + 2 }}
        >
          <div
            className={`cfg-line cfg-line--after${
              selected === CUSTOM ? " is-selected" : ""
            }`}
          >
            {customRow}
          </div>
        </div>
      )}
      <div
        className="cfg-cell cfg-cell--result"
        style={{ gridColumn: 2, gridRow: rows }}
      >
        {resultBox}
      </div>
    </div>
  );
}
